import { X } from "lucide-react";

import { PrincipalRolesCell } from "@/modules/dashboard/settings/components/access/PrincipalRolesCell";
import { useTranslations } from "@/shared/lang/useTranslations";

import type { ApiKeyRow } from "../hooks/useApiKeysCatalog";

export interface ApiKeyAccessCellProps {
  row: ApiKeyRow;
  /** The roles catalog itself 403'd, so nothing can be resolved for any key. */
  catalogUnavailable: boolean;
  /** Opens the assign-roles drawer; absent when the drawer would have nothing truthful to show. */
  onOpen?: () => void;
}

/**
 * What a key can actually do: its assigned roles, plus the effective permission tally resolved deny-first.
 *
 * Three outcomes that must stay distinguishable, and the reason this is not a single number:
 *
 * - **`?`** — the roles could not be read (this key's fetch failed, or the whole catalog 403'd). Unknown.
 * - **`—`** — the roles were read and there are none. The key authorizes nothing; that is a confirmed fact.
 * - **`n/84`** — resolved, with the denied count beside it when any statement subtracts.
 *
 * Collapsing the first two into one glyph is the bug this component exists to prevent: "we could not ask" and
 * "it can do nothing" lead an operator to opposite actions.
 */
export function ApiKeyAccessCell({ row, catalogUnavailable, onOpen }: ApiKeyAccessCellProps) {
  const { t } = useTranslations("settings");

  const unknown = row.rolesFailed || catalogUnavailable;
  const noRoles = !unknown && row.roles.length === 0;

  return (
    <div className="flex flex-col gap-1">
      <PrincipalRolesCell roles={row.roles} rolesFailed={row.rolesFailed} onShowAll={onOpen} />
      {unknown ? (
        <span
          className="font-mono text-[12px] font-semibold text-muted-foreground/70"
          title={
            catalogUnavailable
              ? t("access_effective_catalog_unknown_title")
              : t("access_effective_unknown_title")
          }
        >
          ?
        </span>
      ) : noRoles ? (
        <span className="font-mono text-[11px] text-muted-foreground/60">—</span>
      ) : (
        row.summary && (
          <div className="flex items-center gap-2">
            <span
              className="font-mono text-[12px] font-semibold tabular-nums text-primary"
              title={t("access_effective_granted_title")}
            >
              {row.summary.allowed}
              <span className="font-normal text-muted-foreground/60">/{row.summary.total}</span>
            </span>
            {row.summary.denied > 0 && (
              <span
                className="inline-flex items-center font-mono text-[11px] font-semibold text-destructive"
                title={t("access_effective_denied_title")}
              >
                <X size={9} />
                {row.summary.denied}
              </span>
            )}
          </div>
        )
      )}
    </div>
  );
}
